<template>
  <section class="relative backdrop-blur-[44px] overflow-hidden">
    <div class="max-w-screen-3xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-22">
      <!-- Header Row -->
      <div
        class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-12 md:mb-16 border-t border-gray-200 pt-10 md:pt-20 lg:pt-24"
      >
        <!-- Left: Badge + Title -->
        <div class="flex flex-col gap-6 w-full md:max-w-[520px]">
          <!-- Badge -->

          <UiBaseSectionBadge
            title="Votre Omra en quelques étapes"
            :icon="IconRamadhan02"
          />

          <!-- Title -->
          <h2
            class="w-full text-3xl lg:text-[40px] font-semibold font-outfit text-gray-800 leading-tight tracking-[-0.68px]"
          >
            Configurez votre Omra
            <span class="text-gray-800">en quelques étapes</span>
          </h2>
        </div>

        <!-- Right: CTA Button -->
        <a
          href="/omra"
          class="inline-flex w-fit self-start items-center gap-1 bg-primary hover:bg-primary-amber transition-colors border-2 border-primary/30 rounded-[30px] px-6 py-4 shadow-sm"
        >
          <span class="text-white text-base font-semibold font-outfit">
            Voir nos formules Omra
          </span>
          <svg
            class="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </a>
      </div>

      <!-- Steps Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-[37px] border-b border-gray-200 pb-10 md:pb-20 lg:pb-24"
      >
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="bg-[#fff9ec] border border-[#ffcb58] rounded-[16px] px-5 md:px-6 py-10 md:py-12 lg:py-[70px] flex flex-col items-center text-center relative w-full"
        >
          <div
            class="absolute left-[15px] top-[15px] rounded-[30px] size-[40px] flex items-center justify-center text-white text-[14px] font-semibold font-outfit tracking-[-0.14px]"
            :style="{ backgroundColor: step.badgeColor }"
          >
            {{ step.badge }}
          </div>
          <!-- Icon -->
          <div
            class="mb-2.5 flex items-center justify-center size-[72px]"
            :class="step.iconBg ? 'rounded-[50px] ' : ''"
            :style="step.iconBg ? { backgroundColor: step.iconBg } : undefined"
          >
            <component
              :is="step.icon"
              :size="step.iconSize"
              class="text-primary"
            />
          </div>

          <!-- Title -->
          <h3
            class="text-[20px] font-semibold font-outfit text-[#1f2937] tracking-[-0.2px] leading-[1.2] mb-2.5"
          >
            {{ step.title }}
          </h3>

          <!-- Description -->
          <p
            class="text-[16px] font-normal font-outfit text-[#6b7280] leading-[1.4]"
          >
            {{ step.description }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Component } from "vue";
import {
  IconRamadhan02,
  IconGestionReservations,
  IconAssurance,
  IconPasseport,
  IconKaaba,
} from "~/components/icons";

interface Step {
  icon: Component;
  iconSize: number;
  iconBg?: string;
  title: string;
  description: string;
  badge: string;
  badgeColor: string;
}

const steps: Step[] = [
  {
    icon: IconGestionReservations,
    iconSize: 38,
    iconBg: "#ffeec9",
    title: "Choisir la date et la formule",
    description:
      "Sélectionnez la date de départ qui vous convient et découvrez les détails du programme Omra, la durée du séjour et les prestations incluses.",
    badge: "01",
    badgeColor: "#549f73",
  },
  {
    icon: IconAssurance,
    iconSize: 32,
    iconBg: "#ffeec9",
    title: "Renseigner les informations de réservation",
    description:
      "Sélectionnez la date de départ qui vous convient et découvrez les détails du programme Omra, la durée du séjour et les prestations incluses.",
    badge: "02",
    badgeColor: "#54869f",
  },
  {
    icon: IconPasseport,
    iconSize: 40,
    iconBg: "#ffeec9",
    title: "Télécharger les documents de voyage",
    description:
      "Ajoutez les documents requis (passeport, justificatifs, etc.) pour assurer un traitement rapide et conforme de votre demande.",
    badge: "03",
    badgeColor: "#545e9f",
  },
  {
    icon: IconKaaba,
    iconSize: 32,
    iconBg: "#ffeec9",
    title: "Commencer votre Omra en toute sérénité",
    description:
      "Votre réservation est finalisée : concentrez-vous sur l'essentiel et vivez votre Omra dans la paix, la confiance et le recueillement.",
    badge: "04",
    badgeColor: "#7c549f",
  },
];
</script>
